import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { ProductType } from '../../../hooks/useProducts';
import { formatNumberWithDecimals } from '../../../lib/formatters';
import { Gwei } from '../../../lib/gwei';
import { getDecimalPlaces, isMultipleOf } from '../../../lib/math';
import { Input } from '../../Input/Input';
import { MidPriceButton } from './MidPriceButton';
import { useTradeFormStore } from './TradeFormState';

const errorId = 'trade-form-price-error';

export const TradeFormLimitPriceInputUi: React.FC<{
  product: ProductType;
  MidPriceButton: typeof MidPriceButton;
}> = ({ product, MidPriceButton }) => {
  const orderType = useTradeFormStore((x) => x.orderType);
  const limitPrice = useTradeFormStore((x) => x.limitPrice);
  const setLimitPrice = useTradeFormStore((x) => x.setLimitPrice);
  const priceValidationError = (() => {
    if (limitPrice && limitPrice < product.tickSize) {
      return `Minimum price is ${formatNumberWithDecimals(product.tickSize)}`;
    }
    if (limitPrice && !isMultipleOf(limitPrice, product.tickSize)) {
      return `Price must be a multiple of ${formatNumberWithDecimals(product.tickSize)}`;
    }
    return '';
  })();

  if (orderType === 'MARKET') {
    return null;
  }
  const label = 'Limit Price';

  const priceDecimals = getDecimalPlaces(product.tickSize);

  return (
    <div className="space-x-1">
      <Input
        inputType="number"
        label="Limit Price"
        min={Number(Gwei.fmt(product.minPrice))}
        isAllowed={(values) => {
          const { floatValue = 0 } = values;
          return floatValue < Number(Gwei.fmt(product.maxPrice));
        }}
        step={Number(Gwei.fmt(product.tickSize))}
        name={'limitPrice'}
        aria-label={label}
        hasError={Boolean(priceValidationError)}
        aria-errormessage={priceValidationError ? errorId : undefined}
        aria-required={true}
        inputMode="numeric"
        decimalScale={priceDecimals}
        containerClassName={priceValidationError ? 'border border-borderError' : ''}
        rightElement={<MidPriceButton />}
        value={limitPrice ? Gwei.fmt(limitPrice) : ''}
        onValueChange={(values, sourceInfo) => {
          if (sourceInfo.source !== 'event') return;
          if (values.value === '') {
            setLimitPrice(undefined);
            return;
          }
          setLimitPrice(Gwei.toGwei(values.value));
        }}
      />

      {priceValidationError && (
        <p
          id={errorId}
          className="mt-2 flex items-center gap-1 border-borderError text-xs text-contentRed"
          role="alert"
        >
          <InformationCircleIcon width={16} height={16} className="shrink-0" />
          {priceValidationError}
        </p>
      )}
    </div>
  );
};

export const TradeFormLimitPriceInput: React.FC<{ product: ProductType }> = ({ product }) => {
  return <TradeFormLimitPriceInputUi product={product} MidPriceButton={MidPriceButton} />;
};
